import { Download } from '@mui/icons-material';
import { Box, IconButton, styled, Typography } from '@mui/material';
import { IPublicAndPrivateSettings } from '@pepperhq/location-sdk';
import { ImageFormField } from 'lib/form/fields/ImageFormField';
import { NumberFormField } from 'lib/form/fields/NumberFormField';
import { SwitchFormField } from 'lib/form/fields/SwitchFormField';
import { MuiCardForm } from 'lib/form/MuiCardForm';
import { useDownloadAsset } from 'lib/hooks/useDownloadAsset';
import { SettingDisplay } from 'lib/SettingDisplay';
import { SettingDisplaySwitch } from '../SettingDisplaySwitch';

const PREFIX = 'PreorderSettings';

const classes = {
    settingsCard: `${PREFIX}-settingsCard`,
    displayClassName: `${PREFIX}-displayClassName`,
    image: `${PREFIX}-image`
};

const StyledMuiCardForm = styled(MuiCardForm)(({ theme }) => ({
    [`&.${classes.settingsCard}`]: {
        marginBottom: theme.spacing(2)
    },
    [`& .${classes.displayClassName}`]: {
        padding: theme.spacing(2)
    },
    [`& .${classes.image}`]: {
        width: 'auto',
        height: 'auto',
        maxHeight: '400px',
        maxWidth: '100%'
    }
}));

type IProps = {
    settings: IPublicAndPrivateSettings;
    tenantId: string;
    locationId: string;
    onSubmit: (values: Record<string, any>) => Promise<boolean>;
};

export const PreorderSettings = ({ settings, tenantId, onSubmit, locationId }: IProps) => {
    const { loading: dineInSelectionImageLoading, handleDownload: handleDownloadDineInSelectionImage } =
        useDownloadAsset({
            src: `${import.meta.env.MEDIA_SERVICE_URL}/tenants/${tenantId}/app_assets/web_dine_in_selection`,
            fileName: 'web_dine_in_selection'
        });
    const {
        loading: collectionSelectionImageLoading,
        handleDownload: handleDownloadCollectionSelectionImage
    } = useDownloadAsset({
        src: `${
            import.meta.env.MEDIA_SERVICE_URL
        }/tenants/${tenantId}/app_assets/web_preorder_collection_selection`,
        fileName: 'web_preorder_collection_selection'
    });

    return (
        <StyledMuiCardForm
            title="Preorder"
            initialValues={{
                preOrderEnabled: settings?.preOrderEnabled ?? false,
                asapOrderingEnabled: settings?.asapOrderingEnabled ?? false,
                readyPeriodStart: settings?.preOrder?.readyPeriodStart,
                readyPeriodEnd: settings?.preOrder?.readyPeriodEnd,
                readyPeriodPadding: settings?.preOrder?.readyPeriodPadding,
                // eslint-disable-next-line @typescript-eslint/naming-convention
                capturePhoneNumberOnOrder_PREORDER: settings?.capturePhoneNumberOnOrder?.PREORDER ?? false,
                // eslint-disable-next-line @typescript-eslint/naming-convention
                captureNameOnOrder_PREORDER: settings?.web?.captureNameOnOrder?.PREORDER ?? false,
                dineInImage: `${
                    import.meta.env.MEDIA_SERVICE_URL
                }/tenants/${tenantId}/app_assets/web_dine_in_selection`,
                collectionImage: `${
                    import.meta.env.MEDIA_SERVICE_URL
                }/tenants/${tenantId}/app_assets/web_preorder_collection_selection`
            }}
            onSubmit={onSubmit}
            childrenWhileDisplaying={
                <Box display="flex" flexDirection="column">
                    <SettingDisplaySwitch
                        checked={settings?.preOrderEnabled}
                        isLocation={!!locationId}
                        title="Preorder Enabled"
                    />
                    <SettingDisplaySwitch
                        checked={settings?.asapOrderingEnabled}
                        isLocation={!!locationId}
                        title="ASAP Ordering Enabled"
                    />
                    {locationId === undefined && (
                        <>
                            <SettingDisplay label="Window Start">
                                <Typography variant="body2">
                                    {settings?.preOrder?.readyPeriodStart}
                                </Typography>
                            </SettingDisplay>
                            <SettingDisplay label="Window End">
                                <Typography variant="body2">{settings?.preOrder?.readyPeriodEnd}</Typography>
                            </SettingDisplay>
                            <SettingDisplay label="Window Padding">
                                <Typography variant="body2">
                                    {settings?.preOrder?.readyPeriodPadding}
                                </Typography>
                            </SettingDisplay>
                            <SettingDisplay
                                label="Web Dine In Selection Image"
                                description="Min Ratio 657px X 220px, Max Ratio 800px x 268px PNG or JPEG"
                            >
                                <Box
                                    display="flex"
                                    flexDirection="row"
                                    flexWrap="wrap"
                                    alignItems="center"
                                    gap={1}
                                >
                                    <img
                                        src={`${
                                            import.meta.env.MEDIA_SERVICE_URL
                                        }/tenants/${tenantId}/app_assets/web_dine_in_selection`}
                                        className={classes.image}
                                    />
                                    <IconButton
                                        disabled={dineInSelectionImageLoading}
                                        onClick={handleDownloadDineInSelectionImage}
                                    >
                                        <Download />
                                    </IconButton>
                                </Box>
                            </SettingDisplay>
                            <SettingDisplay
                                label="Web Collection Selection Image"
                                description="Min Ratio 657px X 220px, Max Ratio 800px x 268px PNG or JPEG"
                            >
                                <Box
                                    display="flex"
                                    flexDirection="row"
                                    flexWrap="wrap"
                                    alignItems="center"
                                    gap={1}
                                >
                                    <img
                                        src={`${
                                            import.meta.env.MEDIA_SERVICE_URL
                                        }/tenants/${tenantId}/app_assets/web_preorder_collection_selection`}
                                        className={classes.image}
                                    />
                                    <IconButton
                                        disabled={collectionSelectionImageLoading}
                                        onClick={handleDownloadCollectionSelectionImage}
                                    >
                                        <Download />
                                    </IconButton>
                                </Box>
                            </SettingDisplay>
                        </>
                    )}
                    <SettingDisplaySwitch
                        checked={settings?.capturePhoneNumberOnOrder?.PREORDER}
                        isLocation={!!locationId}
                        title="Capture Phone Number on Preorder"
                    />
                    <SettingDisplaySwitch
                        checked={settings?.web?.captureNameOnOrder?.PREORDER}
                        isLocation={!!locationId}
                        title="Capture Name on Preorder"
                    />
                </Box>
            }
            childrenWhileEditing={
                <Box display="flex" flexDirection="column">
                    <SettingDisplay label="Preorder Enabled">
                        <SwitchFormField name="preOrderEnabled" />
                    </SettingDisplay>
                    <SettingDisplay label="ASAP Ordering Enabled">
                        <SwitchFormField name="asapOrderingEnabled" />
                    </SettingDisplay>
                    {locationId === undefined && (
                        <>
                            <SettingDisplay label="Window Start">
                                <NumberFormField name="readyPeriodStart" />
                            </SettingDisplay>
                            <SettingDisplay label="Window End">
                                <NumberFormField name="readyPeriodEnd" />
                            </SettingDisplay>
                            <SettingDisplay label="Window Padding">
                                <NumberFormField name="readyPeriodPadding" />
                            </SettingDisplay>
                            <SettingDisplay
                                label="Web Dine In Selection Image"
                                description="Min Ratio 657px X 220px, Max Ratio 800px x 268px PNG or JPEG"
                            >
                                <ImageFormField
                                    name="dineInImage"
                                    aspect={657 / 220}
                                    fileName="web_dine_in_selection"
                                    uploadFolder="app_assets"
                                    unremovable
                                />
                            </SettingDisplay>
                            <SettingDisplay
                                label="Web Collection Selection Image"
                                description="Min Ratio 657px X 220px, Max Ratio 800px x 268px PNG or JPEG"
                            >
                                <ImageFormField
                                    name="collectionImage"
                                    aspect={657 / 220}
                                    fileName="web_preorder_collection_selection"
                                    uploadFolder="app_assets"
                                    unremovable
                                />
                            </SettingDisplay>
                        </>
                    )}
                    <SettingDisplay label="Capture Phone Number on Preorder">
                        <SwitchFormField name="capturePhoneNumberOnOrder_PREORDER" />
                    </SettingDisplay>
                    <SettingDisplay label="Capture Name on Preorder">
                        <SwitchFormField name="captureNameOnOrder_PREORDER" />
                    </SettingDisplay>
                </Box>
            }
            className={classes.settingsCard}
            boxClassName={classes.displayClassName}
        />
    );
};
